import React, {
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type TransitionEvent,
} from "react";

import "./stylesCollapse.css";

export type CollapseProps = {
  /** Controlled open state. */
  open: boolean;
  /** Content to show/hide. */
  children: ReactNode;
  /** Extra className for the outer wrapper. */
  className?: string;
  /** Animation duration in ms. Default 200. */
  durationMs?: number;
  /**
   * If false, unmounts children when closed. Default true — keep them mounted
   * so inputs don't lose focus/value mid-animation.
   */
  keepMounted?: boolean;
  /** Forwarded to the wrapper element. */
  "data-testid"?: string;
};

/**
 * Controlled vertical collapse. Animates max-height + opacity.
 *
 * The consumer renders its own trigger button — this only wraps the panel.
 * After opening, overflow flips to visible so popovers inside (e.g. antd
 * Select dropdowns) aren't clipped.
 */
const Collapse = forwardRef<HTMLDivElement, CollapseProps>(
  (
    {
      open,
      children,
      className = "",
      durationMs = 200,
      keepMounted = true,
      "data-testid": dataTestId,
    },
    ref,
  ) => {
    const innerRef = useRef<HTMLDivElement | null>(null);
    const [maxHeight, setMaxHeight] = useState<number>(open ? -1 : 0);
    const [overflowVisible, setOverflowVisible] = useState<boolean>(open);

    // Measure on open-flip only. Excluding `children` from deps is deliberate:
    // every parent render passes fresh JSX, which would re-trigger measure +
    // setState on every keystroke up the tree. Consumers with dynamic content
    // can remount via `key` or call out for ResizeObserver later.
    useLayoutEffect(() => {
      const el = innerRef.current;
      if (!el) return;
      const h = el.scrollHeight;
      let rafId: number | null = null;
      if (open) {
        // Going open: lock overflow so the transition reads cleanly, then
        // measure and apply the real height.
        setOverflowVisible(false);
        setMaxHeight((prev) => (prev === h ? prev : h));
      } else {
        // Going closed: snap to current pixel height first so the transition
        // has somewhere to animate from. Skip the rAF when nothing was
        // measured (e.g. jsdom) — there's nothing to animate.
        setOverflowVisible(false);
        if (h > 0) {
          setMaxHeight(h);
          rafId = requestAnimationFrame(() => setMaxHeight(0));
        } else {
          setMaxHeight(0);
        }
      }
      return () => {
        if (rafId !== null) cancelAnimationFrame(rafId);
      };
    }, [open]);

    useEffect(() => {
      const el = innerRef.current;
      if (!open || !el || typeof ResizeObserver === "undefined") {
        return undefined;
      }

      const observer = new ResizeObserver(() => {
        const nextHeight = el.scrollHeight;
        setMaxHeight((prev) => (prev === nextHeight ? prev : nextHeight));
      });

      observer.observe(el);
      return () => observer.disconnect();
    }, [open]);

    useEffect(() => {
      const el = innerRef.current;
      if (!open || !el || typeof MutationObserver === "undefined") {
        return undefined;
      }

      const observer = new MutationObserver(() => {
        const nextHeight = el.scrollHeight;
        setMaxHeight((prev) => (prev === nextHeight ? prev : nextHeight));
      });

      observer.observe(el, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
      });

      return () => observer.disconnect();
    }, [open]);

    const handleTransitionEnd = useCallback(
      (event: TransitionEvent<HTMLDivElement>) => {
        if (event.propertyName !== "max-height") return;
        if (open) {
          // Drop the pixel cap so nested panels (e.g. task accordions inside
          // a challenge accordion) can grow the parent after they open.
          setMaxHeight(-1);
          // Now safe to let dropdowns spill out of the wrapper.
          setOverflowVisible(true);
        }
      },
      [open],
    );

    if (!open && !keepMounted) return null;

    const style: React.CSSProperties = {
      maxHeight: maxHeight < 0 ? "none" : `${maxHeight}px`,
      opacity: open ? 1 : 0,
      overflow: overflowVisible ? "visible" : "hidden",
      transitionDuration: `${durationMs}ms`,
    };

    return (
      <div
        ref={(node) => {
          innerRef.current = node;
          if (typeof ref === "function") ref(node);
          else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }}
        className={`pam-collapse ${className}`.trim()}
        style={style}
        aria-hidden={!open}
        data-testid={dataTestId}
        onTransitionEnd={handleTransitionEnd}
      >
        {children}
      </div>
    );
  },
);

Collapse.displayName = "Collapse";

export default Collapse;
